import React, { useEffect, useMemo } from 'react';
import {
  BankIcon,
  BillingCheckIcon,
  Box,
  Controller,
  Preloader,
  Text,
  useFormContext,
  useWatch,
} from '@common-fe/common-fe';
import { RadioButton } from 'grommet';
import _ from 'lodash';

import { PaymentMethod, PaymentMethodType } from '@/modules/employee/employee.types';
import { useGetEmployeeById } from '@/modules/employee/Employee/hooks';
import { getFullAddress } from '@/utils';

import { CIP_SYNTHETIC_ID, CLOSE_CONTRIBUTION_SIDE, EmployeeCIPCloseForm } from '../../cip.constants';
import { usePaymentMethodsQuery } from '../queries';
interface Props {
  employeeId: string;
  isPendingPresent?: boolean;
}
const EmployeeCIPCLosePayments: React.FC<Props> = ({ employeeId, isPendingPresent }) => {
  const { isLoading, formattedData, isSuccess } = usePaymentMethodsQuery({
    employeeId: employeeId,
  });
  const { data } = useGetEmployeeById(employeeId);

  const { control, setValue } = useFormContext<EmployeeCIPCloseForm>();
  const directDepositItems = useMemo(() => {
    return formattedData.filter((item) => item.type === PaymentMethodType.DIRECT_DEPOSIT);
  }, [formattedData]);
  const checkItems = useMemo(() => {
    const list = formattedData.filter((item) => item.type === PaymentMethodType.CHECK);
    if (!list.length) {
      const syntheticCheck: PaymentMethod = {
        id: CIP_SYNTHETIC_ID,
        accountDescription: getFullAddress({
          line1: data?.physicalFirstAddressLine,
          line2: data?.physicalSecondAddressLine,
          city: data?.physicalCity,
          state: data?.physicalState
            ? {
                name: data?.physicalState,
              }
            : undefined,
          zipcode: data?.physicalZipCode,
        }),
      };
      return [syntheticCheck];
    }
    return list;
  }, [
    data?.physicalCity,
    data?.physicalFirstAddressLine,
    data?.physicalSecondAddressLine,
    data?.physicalState,
    data?.physicalZipCode,
    formattedData,
  ]);
  const isAddressPresent = useMemo(() => {
    return data?.physicalFirstAddressLine || data?.physicalSecondAddressLine;
  }, [data?.physicalFirstAddressLine, data?.physicalSecondAddressLine]);
  const contributionTo: string = useWatch({
    control,
    name: 'contributionTo',
  });
  useEffect(() => {
    if (isSuccess) {
      if (formattedData.length) {
        const firstPaymentMethod = _.get(formattedData, '[0].id');
        setValue('payment', firstPaymentMethod);
      } else {
        const firstPaymentMethod = _.get(checkItems, '[0].id');
        setValue('payment', firstPaymentMethod);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkItems, formattedData, isSuccess, isPendingPresent, contributionTo]);
  const isHiddenDirect = useMemo(() => contributionTo !== CLOSE_CONTRIBUTION_SIDE.EMPLOYEE, [contributionTo]);
  if (isHiddenDirect && !isPendingPresent) {
    return null;
  }
  if (isLoading) {
    return <Preloader testId="cip_preloader" />;
  }
  return (
    <Box
      data-testid="cip_payment"
      margin={{
        top: 'spacing24',
      }}
    >
      <Text margin={{ bottom: 'spacing24' }}>Select method for returning employee funds:</Text>
      {!!directDepositItems.length && (
        <Box
          round="container1Round"
          border={{ size: 'small', color: 'border2' }}
          background="canvas"
          pad="spacing24"
          direction="column"
          margin={{ bottom: 'spacing24' }}
        >
          <Box direction="row" align="center">
            <Box width="48px" height="48px" align="center" justify="center">
              <BankIcon size="40px" color="iconAccent" />
            </Box>

            <Text size="large" weight={700} margin={{ left: 'spacing12' }}>
              Direct deposit
            </Text>
          </Box>
          <Box margin={{ vertical: 'spacing12' }} height="1px" background="border1"></Box>

          <Controller
            name="payment"
            control={control}
            render={({ field }) => (
              <Box>
                {directDepositItems.map((item) => (
                  <Box
                    key={item.id}
                    margin={{ bottom: _.last(formattedData) === item ? '' : 'spacing12' }}
                    pad={{ left: 'spacing12' }}
                  >
                    <RadioButton
                      onChange={() => field.onChange(item.id)}
                      name={item.id}
                      data-testId={`cip_payment_direct-deposit_${item.id}_button`}
                      checked={field.value === item.id}
                      label={
                        <Text size="large">
                          {item.accountDescription} (•••• {item.accountNumberLast4})
                        </Text>
                      }
                    />
                  </Box>
                ))}
              </Box>
            )}
          />
        </Box>
      )}
      {isAddressPresent && (
        <Box
          round="container1Round"
          border={{ size: 'small', color: 'border2' }}
          background="canvas"
          pad="spacing24"
          direction="column"
        >
          <Box direction="row" align="center">
            <Box width="48px" height="48px" align="center" justify="center">
              <BillingCheckIcon size="40px" color="iconAccent" />
            </Box>

            <Box direction="column" margin={{ left: 'spacing12' }}>
              <Box direction="row" align="center">
                <Text size="large" weight={700}>
                  Check
                </Text>
                {/* <Box
                round="dropdownItemRound"
                pad={{ horizontal: 'spacing6' }}
                background="successContainer"
                margin={{ left: 'spacing8' }}
              >
                <Text color="success" size="small" weight={700}>
                  Free
                </Text>
              </Box> */}
              </Box>
              <Text weight={400} color="textSecondary">
                We will send a check to your Mailing address.
              </Text>
            </Box>
          </Box>
          <Box margin={{ vertical: 'spacing12' }} height="1px" background="border1"></Box>

          <Controller
            name="payment"
            control={control}
            render={({ field }) => (
              <Box>
                {checkItems.map((item) => {
                  return (
                    <Box key={item.id} margin={{ bottom: 'spacing12' }} pad={{ left: 'spacing12' }}>
                      <RadioButton
                        onChange={() => field.onChange(item.id)}
                        data-testId={`cip_payment_check_${item.id}_button`}
                        name={item.id}
                        checked={field.value === item.id}
                        label={<Text size="large">{item.accountDescription}</Text>}
                      />
                    </Box>
                  );
                })}
              </Box>
            )}
          />
        </Box>
      )}
    </Box>
  );
};

export default EmployeeCIPCLosePayments;
